import { useState } from 'react';
import { mutate as globalMutate } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import { FormKeyObject } from '@/models/forms/backend-types/FormDataAppointment.ts';
import { FormDataCode } from '@/models/forms/backend-types/FormDataCode.ts';
import { FormTemplate } from '@/models/forms/backend-types/form-template/FormTemplate.ts';
import { FormsFetch, FormsResolution } from '@/models/forms/forms-fetch.ts';
import { InsuranceType } from '@/models/insurance.ts';

import {
    getAppointmentSpecificFormsData,
    getFormTemplates,
    getRequiredAppointmentForms,
    getSharedDocuments,
} from '@/services/formDataService.ts';
import { showErrorNotification, showLoadingNotification } from '@/services/notificationService.ts';

import { getInstanceId } from '@/utils/appointmentUtils.ts';
import { getSelectedLocation } from '@/utils/configUtils.ts';
import { getCustomerId } from '@/utils/customer-utils.ts';
import { formatRequiredForms, getFormsFetchData, getFormsResolution, hasFormsToFill } from '@/utils/formUtils.ts';
import { getValidInsuranceTypeFromAppointment } from '@/utils/insurance-utils.ts';
import { openQrCodeModal } from '@/utils/modalUtils.tsx';
import { getUnregisteredUserId, hasResolvablePatientId } from '@/utils/patientUtils.ts';
import { joinAppointmentServicesKeys } from '@/utils/practitioner/cachedDataUtils.ts';

const APPOINTMENT_FORMS_KEY_PREFIX = 'appointment_forms';

export const revalidateAppointmentFormsCache = (appointmentId: string): void => {
    void globalMutate(
        (key) => Array.isArray(key) && key[0] === APPOINTMENT_FORMS_KEY_PREFIX && key.at(-1) === appointmentId
    );
};

type AppointmentFormsData = Awaited<ReturnType<typeof getAppointmentSpecificFormsData>>;

const resolveFormsState = (
    hasPatient: boolean,
    isLoading: boolean,
    professionalFormsFetch: FormsFetch<FormTemplate[]> | undefined,
    appointmentData: AppointmentFormsData | undefined
): FormsResolution => {
    if (!hasPatient) {
        return FormsResolution.NOT_APPLICABLE;
    }

    if (isLoading || !professionalFormsFetch || !appointmentData) {
        return FormsResolution.LOADING;
    }

    return getFormsResolution(
        appointmentData.appointment_forms,
        professionalFormsFetch,
        appointmentData.selected_forms
    );
};

interface PatientAppointmentFormsHook {
    formatted_forms_for_appointment: FormattedForm[] | undefined;
    professional_form_templates: FormTemplate[] | undefined;
    /** Whether the required set is known. Callers must not read the forms as authoritative unless it is. */
    forms_resolution: FormsResolution;
    mutateAppointmentForms: () => Promise<void>;
    selected_appointment_forms: FormKeyObject[] | undefined;
    is_loading_forms: boolean;
    form_data_code: FormDataCode | undefined;
    is_lazy_loaded: boolean;
    showQrCode: () => void;
    triggerAppointmentFormsData: () => void;
    resolveForms: () => Promise<FormattedForm[] | undefined>;
}

export const usePatientAppointmentForms = (
    appointment: DailyAppointment,
    options?: { lazy?: boolean; insuranceType?: InsuranceType }
): PatientAppointmentFormsHook => {
    const [should_load, setShouldLoad] = useState(!options?.lazy);
    // When provided (e.g. an insurance type chosen at check-in but not yet persisted), the required
    // documents are fetched for this type instead of the appointment's stored insurance_coverage_type.
    // It is part of the SWR key so changing the selection reactively refetches the correct forms.
    const insurance_type_override = options?.insuranceType;

    const user_id = getUnregisteredUserId(appointment);
    const customer_id = getCustomerId();
    const selected_location = getSelectedLocation();

    // Both of these change which forms are required, so either changing has to miss the cache rather
    // than serve the answer computed for the previous value.
    const appointmentInsuranceType = getValidInsuranceTypeFromAppointment(appointment);
    const serviceKeys = joinAppointmentServicesKeys(appointment);

    // Every form-data endpoint is scoped by patient, so with nobody attached all four can only be
    // rejected. Nothing is requested at all in that case.
    const hasPatient = hasResolvablePatientId(appointment);
    const shouldFetch = should_load && hasPatient;

    // Keyed only by professional.id — shared across all patients for the same professional
    const {
        isLoading: is_loading_templates,
        mutate: mutate_templates,
        data: professionalFormsFetch,
    } = useSWRImmutable(shouldFetch ? ['form_templates', customer_id, appointment.professional.id] : undefined, () =>
        getFormTemplates(appointment.professional.id, customer_id)
    );

    // Keyed only by user_id - the shared documents by the user must be fetched only once by each user/patient
    const {
        isLoading: is_loading_shared_documents,
        mutate: mutate_shared_documents,
        data: shared_documents,
    } = useSWRImmutable(shouldFetch ? ['shared_documents', customer_id, user_id] : undefined, () =>
        getSharedDocuments(user_id, customer_id)
    );

    // appointment.id stays last: revalidateAppointmentFormsCache matches on it.
    const {
        isLoading: is_loading_appointment,
        mutate: mutate_appointment,
        data: appointment_data,
    } = useSWRImmutable(
        shouldFetch
            ? [
                  APPOINTMENT_FORMS_KEY_PREFIX,
                  customer_id,
                  user_id,
                  selected_location.id,
                  insurance_type_override,
                  appointmentInsuranceType,
                  serviceKeys,
                  appointment.id,
              ]
            : undefined,
        () =>
            getAppointmentSpecificFormsData(
                user_id,
                customer_id,
                appointment,
                selected_location,
                insurance_type_override
            )
    );

    const is_loading_forms = is_loading_templates || is_loading_shared_documents || is_loading_appointment;

    const professional_form_templates = professionalFormsFetch ? getFormsFetchData(professionalFormsFetch) : undefined;

    const formsResolution = resolveFormsState(hasPatient, is_loading_forms, professionalFormsFetch, appointment_data);

    let formatted_forms_for_appointment: FormattedForm[] | undefined;
    if (formsResolution === FormsResolution.RESOLVED && professional_form_templates && appointment_data) {
        formatted_forms_for_appointment = formatRequiredForms(
            appointment_data.selected_forms,
            getFormsFetchData(appointment_data.appointment_forms),
            professional_form_templates,
            shared_documents,
            appointment_data.form_data_code,
            appointment
        );
    }

    const mutateAppointmentForms = async (): Promise<void> => {
        await Promise.all([mutate_templates(), mutate_shared_documents(), mutate_appointment()]);
    };

    // Fetches the appointment's required forms on demand. Used as a fallback when the reactive
    // formatted_forms_for_appointment is undefined (e.g. the form templates request errored), so the
    // check-in flow can still generate the forms code instead of silently skipping it.
    const resolveForms = async (): Promise<FormattedForm[] | undefined> => {
        const requiredForms = await getRequiredAppointmentForms(appointment, selected_location);
        return requiredForms.formatted_forms_for_appointment;
    };

    const showQrCode = (): void => {
        // The required set gates the code: issuing one for forms we cannot name would hand the patient
        // the wrong questionnaire, so say why instead of doing nothing.
        if (formsResolution === FormsResolution.LOADING) {
            showLoadingNotification('Code wird geladen, bitte einen Moment warten und erneut versuchen.');
            return;
        }

        if (formsResolution === FormsResolution.UNRESOLVED) {
            showErrorNotification(
                'Formularbedarf konnte nicht ermittelt werden. Bitte Versicherungsart und Terminart prüfen.'
            );
            return;
        }

        if (!hasFormsToFill(formatted_forms_for_appointment)) {
            return;
        }

        if (appointment_data?.form_data_code) {
            openQrCodeModal(appointment_data.form_data_code.code, getInstanceId(appointment) ?? '');
        } else {
            showErrorNotification('Es ist ein Problem bei der Erstellung des QR-Codes aufgetreten.');
        }
    };

    return {
        formatted_forms_for_appointment: formatted_forms_for_appointment,
        professional_form_templates: professional_form_templates,
        forms_resolution: formsResolution,
        selected_appointment_forms: appointment_data?.selected_forms,
        is_loading_forms: is_loading_forms,
        form_data_code: appointment_data?.form_data_code,
        is_lazy_loaded: should_load,
        mutateAppointmentForms: mutateAppointmentForms,
        showQrCode: showQrCode,
        triggerAppointmentFormsData: () => setShouldLoad(true),
        resolveForms: resolveForms,
    };
};
